import React from 'react';
import { Link } from 'react-router-dom';
import buildingImg from '../assets/building.png';
import SEO from '../components/SEO';

const NRI = () => {
  const whyInvest = [
    { icon: 'handshake', title: 'Trusted Real Estate Partner' },
    { icon: 'location_on', title: 'Strategic Locations Across India' },
    { icon: 'description', title: 'Transparent Business Practices' },
    { icon: 'trending_up', title: 'Strong Appreciation Potential' },
    { icon: 'apartment', title: 'Residential & Commercial Projects' },
    { icon: 'support_agent', title: 'Dedicated Investment Support' },
  ];

  const opportunities = [
    { title: 'Residential Projects', desc: 'Luxury apartments, villas, and gated communities crafted for modern lifestyles.', icon: 'home' },
    { title: 'Commercial Properties', desc: 'Office spaces, retail outlets, and business parks in prime locations for high returns.', icon: 'apartment' },
    { title: 'Land Investments', desc: 'Premium residential and commercial plots with excellent appreciation potential.', icon: 'map' },
    { title: 'Joint Development', desc: 'Partnership opportunities for landowners and investors to create exceptional value.', icon: 'handshake' },
  ];

  return (
    <div className="bg-[#041424] min-h-screen w-full font-sans text-white">
      <SEO 
        title="Investor Relations | HBB0011 REALTY"
        description="Explore premium real estate investment opportunities with HBB0011 Realty. Transparent, strategic, and high-appreciation potential projects."
        keywords="real estate investment, property investment India, HBB0011 Realty investors, commercial property, residential investment"
      />
      
      {/* 1. Hero Section (No Founder Image) */}
      <section className="relative w-full pt-32 pb-24 md:pt-40 md:pb-32 flex items-center overflow-hidden border-b border-gray-800">
        <div className="absolute inset-0 z-0">
          <img src={buildingImg} alt="Modern Building" className="w-full h-full object-cover opacity-20 mix-blend-luminosity" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#041424] via-[#041424]/90 to-transparent"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
          {/* Breadcrumb */}
          <div className="flex items-center gap-2 text-xs sm:text-sm font-bold tracking-widest mb-6 uppercase">
            <Link to="/" className="text-white hover:text-[#D4AF37]">Home</Link>
            <span className="text-gray-500 material-symbols-outlined text-sm">chevron_right</span>
            <span className="text-[#D4AF37]">Investor Relations</span>
          </div>

          <div className="max-w-3xl">
            <h1 className="text-5xl sm:text-6xl md:text-7xl font-bold text-white mb-6 leading-tight tracking-tight uppercase">
              Invest with <br/><span className="text-[#D4AF37]">Confidence</span>
            </h1>
            <h2 className="text-xl md:text-2xl text-gray-300 font-light mb-8 uppercase tracking-widest leading-relaxed">
              Building Wealth Through<br/>Smart Real Estate Investments
            </h2>
            <p className="text-sm sm:text-base text-gray-400 mb-10 max-w-xl leading-relaxed">
              At HBB0011 Realty Realtors, we create exceptional value for investors through strategic locations, premium projects, and transparent business practices.
            </p>
          </div>
        </div>
      </section>

      {/* 2. Why Invest Features */}
      <section className="py-16 border-b border-gray-800 bg-[#06121e]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-8">
            {whyInvest.map((item, index) => (
              <div key={index} className="flex flex-col items-center text-center group">
                <div className="w-14 h-14 rounded-full border border-[#D4AF37]/30 flex items-center justify-center mb-4 group-hover:bg-[#D4AF37]/10 transition-colors">
                  <span className="material-symbols-outlined text-[#D4AF37] text-2xl font-light">{item.icon}</span>
                </div>
                <span className="text-[10px] text-white font-bold uppercase tracking-widest">{item.title}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 3. Investment Opportunities */}
      <section className="py-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-2xl sm:text-3xl font-bold text-white uppercase tracking-widest mb-4">Investment Opportunities</h2>
          <div className="w-24 h-1 bg-[#D4AF37] mx-auto"></div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {opportunities.map((opp, index) => (
            <div key={index} className="bg-[#06121e] border border-gray-800 rounded-sm overflow-hidden group hover:border-[#D4AF37]/50 transition-all duration-300">
              <div className="h-48 overflow-hidden relative">
                <img src={buildingImg} alt={opp.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                <div className="absolute -bottom-6 left-1/2 -translate-x-1/2 w-12 h-12 bg-[#041424] border border-[#D4AF37] rounded-full flex items-center justify-center">
                  <span className="material-symbols-outlined text-[#D4AF37] text-xl">{opp.icon}</span>
                </div>
              </div>
              <div className="p-6 text-center pt-10">
                <h3 className="text-xs font-bold text-[#D4AF37] mb-3 uppercase tracking-widest">{opp.title}</h3>
                <p className="text-gray-400 text-[10px] leading-relaxed">{opp.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* 4. Commitment & Process Section */}
      <section className="py-20 bg-[#06121e] border-y border-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-12">
          
          <div className="lg:col-span-4 border-b lg:border-b-0 lg:border-r border-gray-800 pb-10 lg:pb-0 lg:pr-10">
            <h3 className="text-lg font-bold text-white uppercase tracking-widest mb-8 border-b border-gray-800 pb-4">Why Investors Choose Us</h3>
            <ul className="space-y-4">
              {['Professional project management', 'Market research and analysis', 'Transparent documentation', 'Long-term value creation', 'Legal due diligence', 'Personalized investment advisory'].map((item, i) => (
                <li key={i} className="flex items-center gap-3 text-gray-400 text-xs">
                  <span className="material-symbols-outlined text-[#D4AF37] text-base">check_circle</span>
                  {item}
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-8">
            <h3 className="text-lg font-bold text-white uppercase tracking-widest mb-8 border-b border-gray-800 pb-4">Our Investment Process</h3>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
               {['Consultation', 'Property Selection', 'Site Visit', 'Documentation', 'Secure Investment', 'After-Sales Support'].map((step, i) => (
                 <div key={i} className="flex flex-col items-center text-center">
                   <div className="w-12 h-12 rounded-full bg-[#041424] border border-[#D4AF37] flex items-center justify-center text-[#D4AF37] font-bold text-xs mb-3">
                     {i + 1}
                   </div>
                   <h4 className="text-white text-[10px] font-bold uppercase tracking-widest mb-1">{step}</h4>
                 </div>
               ))}
            </div>
          </div>
        </div>
      </section>

      {/* 5. CTA Bar */}
      <section className="bg-[#041424] py-16 lg:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 bg-[#06121e] border border-gray-800 p-8 md:p-10 rounded-sm flex flex-col lg:flex-row items-center justify-between gap-8">
          
          <div className="flex items-center gap-6">
            <div className="w-16 h-16 rounded-full border border-[#D4AF37]/30 flex items-center justify-center shrink-0">
               <span className="material-symbols-outlined text-[#D4AF37] text-3xl font-light">domain_add</span>
            </div>
            <div>
              <h3 className="text-xl sm:text-2xl text-white font-light mb-2">Ready to Elevate Your Business?</h3>
              <p className="text-gray-400 text-xs">Partner with HBB0011 Realty Realtors and unlock exceptional opportunities in real estate.</p>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 w-full lg:w-auto">
            <button className="bg-[#D4AF37] text-[#041424] px-6 py-4 rounded-sm font-bold text-[10px] sm:text-xs uppercase tracking-widest hover:bg-white transition-colors flex items-center justify-center gap-2 w-full sm:w-auto">
              Schedule Consultation <span className="material-symbols-outlined text-base">event</span>
            </button>
            <button className="border border-white text-white px-6 py-4 rounded-sm font-bold text-[10px] sm:text-xs uppercase tracking-widest hover:bg-white hover:text-[#041424] transition-colors flex items-center justify-center gap-2 w-full sm:w-auto">
              Download Brochure <span className="material-symbols-outlined text-base">download</span>
            </button>
            <a href="tel:+919916710011" className="border border-gray-700 text-white px-6 py-4 rounded-sm font-bold text-[10px] sm:text-xs uppercase tracking-widest hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors flex items-center justify-center gap-2 w-full sm:w-auto">
              <span className="material-symbols-outlined text-[#D4AF37] text-base">call</span> +91 99167 10011
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default NRI;